import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { C } from "@/theme";
import { fmtDate } from "@/format";
import { Badge, Chips, LoadingState, ErrorState, EmptyState, ConfirmDialog } from "@/ui";
import { PageHeader } from "@/components/AppShell";

const SEV_VARIANT = { Minor: "warning", Moderate: "error", Severe: "error" };
const SEV_COLOR = { Minor: "#E08A00", Moderate: "#EA6A2C", Severe: "#E23D3D" };

export default function Incidents() {
  const [filter, setFilter] = useState("All");
  const [confirm, setConfirm] = useState(null);
  const { canEdit, user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isRider = user?.role === "Rider" && !!user.rider_id;

  const q = useQuery({ queryKey: ["accidents"], queryFn: () => api.get("/accidents") });
  const del = useMutation({ mutationFn: (id) => api.del(`/accidents/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["accidents"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); } });

  const list = useMemo(() => {
    let l = q.data ?? [];
    if (isRider) l = l.filter((a) => a.rider_id === user.rider_id);
    if (filter !== "All") l = l.filter((a) => a.severity === filter);
    return l;
  }, [q.data, filter, isRider, user]);

  return (
    <div>
      <PageHeader title="Incidents" subtitle="Safety log" action={canEdit ? <button data-testid="incident-add" onClick={() => navigate("/incident-form")} style={{ background: C.brandPrimary }} className="w-11 h-11 rounded-xl flex items-center justify-center"><Plus size={20} color="#fff" /></button> : null} />
      <div className="mb-4"><Chips testID="incident-severity-filter" value={filter} onChange={setFilter} options={[{ key: "All", label: "All" }, { key: "Minor", label: "Minor" }, { key: "Moderate", label: "Moderate" }, { key: "Severe", label: "Severe" }]} /></div>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState onRetry={q.refetch} /> : list.length === 0 ? (
        <EmptyState icon={<ShieldCheck size={44} color={C.success} />} title="No incidents logged" subtitle="Great safety record!" />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map((item) => (
            <div key={item.id} data-testid={`incident-${item.id}`} style={{ background: C.surfaceSecondary, borderColor: C.border, borderLeftColor: SEV_COLOR[item.severity] ?? C.warning, borderLeftWidth: 5 }} className="rounded-2xl border p-4 flex flex-col gap-2">
              <div className="flex items-start gap-2.5">
                <div className="flex-1"><div className="flex items-center gap-2"><span style={{ color: C.onSurface }} className="font-extrabold">{item.rider_name}</span><Badge label={item.severity} variant={SEV_VARIANT[item.severity] ?? "warning"} /></div><p style={{ color: C.muted }} className="text-xs mt-0.5">{fmtDate(item.date)} · {item.location}</p>{item.reported_by_name ? <p style={{ color: C.muted }} className="text-xs mt-0.5">Reported by {item.reported_by_name}</p> : null}</div>
                {isAdmin ? <div className="flex gap-2.5"><button data-testid={`incident-edit-${item.id}`} onClick={() => navigate(`/incident-form?id=${item.id}`)}><Pencil size={18} color={C.onSurface} /></button><button data-testid={`incident-delete-${item.id}`} onClick={() => setConfirm(item)}><Trash2 size={18} color={C.error} /></button></div> : null}
              </div>
              <p style={{ color: C.onSurfaceSecondary }} className="text-sm leading-relaxed">{item.description}</p>
              <div className="flex gap-2 flex-wrap">{item.follow_up_required ? <Badge label="Follow-up required" variant="warning" /> : null}<Badge label={item.resolved ? "Resolved" : "Open"} variant={item.resolved ? "success" : "neutral"} /></div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog open={!!confirm} title="Delete this incident?" message="This safety record will be removed." onCancel={() => setConfirm(null)} onConfirm={() => { if (confirm) del.mutate(confirm.id); setConfirm(null); }} />
    </div>
  );
}
